import React from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts';
import { Patient, FamilyDigestSubject } from '../types';
import { Activity, Heart, Wind, Thermometer, Stethoscope, BedDouble, MessageSquareHeart } from 'lucide-react';

interface DashboardProps {
  patients: Patient[];
  onSelectPatient: (patient: Patient) => void;
  onOpenDocForPatient: (patient: Patient) => void;
  onOpenFamilyDigest?: (subject: FamilyDigestSubject) => void;
}

const HOURLY_ADMISSIONS_DATA = [
  { hour: '02:00', admissions: 3, discharges: 1, capacity: 82 },
  { hour: '04:00', admissions: 5, discharges: 0, capacity: 84 },
  { hour: '06:00', admissions: 9, discharges: 2, capacity: 88 },
  { hour: '08:00', admissions: 14, discharges: 7, capacity: 91 },
  { hour: '10:00', admissions: 18, discharges: 11, capacity: 93 },
  { hour: '12:00', admissions: 15, discharges: 14, capacity: 90 },
  { hour: '14:00', admissions: 12, discharges: 16, capacity: 87 },
  { hour: '16:00', admissions: 16, discharges: 10, capacity: 89 }
];

const DEPARTMENT_STRESS_DATA = [
  { name: 'ICU', load: 93 },
  { name: 'Cardio', load: 84 },
  { name: 'ER', load: 91 },
  { name: 'Neuro', load: 68 },
  { name: 'Oncology', load: 74 },
  { name: 'Peds', load: 55 }
];

export const Dashboard: React.FC<DashboardProps> = ({
  patients,
  onSelectPatient,
  onOpenDocForPatient,
  onOpenFamilyDigest
}) => {
  const handleLaunchPatientDigest = (pt: Patient) => {
    if (!onOpenFamilyDigest) return;
    const subject: FamilyDigestSubject = {
      id: pt.id,
      name: pt.fullName,
      recipientName: pt.familyContact.name,
      recipientRelation: pt.familyContact.relation,
      recipientPhone: pt.familyContact.phone,
      sleepScoreOrState: 'Resting comfortably on telemetry monitoring',
      vitalsSummary: `BP ${pt.vitals.bpSystolic}/${pt.vitals.bpDiastolic} mmHg, Heart rate ${pt.vitals.heartRate} bpm, Oxygen ${pt.vitals.spO2}%`,
      nutritionActivitySummary: `Bedside meal intake completed on schedule, attending physician: ${pt.attendingPhysician}`,
      locationOrRoom: `${pt.department} (${pt.bedNumber})`
    };
    onOpenFamilyDigest(subject);
  };

  return (
    <div className="space-y-6">
      {/* Visual Analytics Chart Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-slate-900/60 border border-slate-800 rounded-2xl p-5 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-slate-100 flex items-center gap-2">
                <Activity className="h-4 w-4 text-teal-400" />
                <span>Patient Flow & Hospital Capacity Telemetry</span>
              </h3>
              <p className="text-xs text-slate-400">Hourly admissions vs discharges and net bed occupancy %</p>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1.5 text-slate-400">
                <span className="h-2 w-2 rounded-full bg-teal-400"></span> Admissions
              </span>
              <span className="flex items-center gap-1.5 text-slate-400">
                <span className="h-2 w-2 rounded-full bg-cyan-400"></span> Occupancy %
              </span>
            </div>
          </div>

          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={HOURLY_ADMISSIONS_DATA}>
                <defs>
                  <linearGradient id="colorAdmit" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorCap" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="hour" stroke="#64748b" tick={{ fontSize: 11 }} />
                <YAxis stroke="#64748b" tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0f172a',
                    borderColor: '#334155',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Area type="monotone" dataKey="admissions" stroke="#14b8a6" fillOpacity={1} fill="url(#colorAdmit)" strokeWidth={2} />
                <Area type="monotone" dataKey="capacity" stroke="#06b6d4" fillOpacity={1} fill="url(#colorCap)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-slate-100">Division Strain Index</h3>
              <p className="text-xs text-slate-400">Bed saturation percentage</p>
            </div>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-800 text-teal-300">
              REAL-TIME
            </span>
          </div>

          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={DEPARTMENT_STRESS_DATA} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                <XAxis type="number" stroke="#64748b" domain={[0, 100]} tick={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" stroke="#64748b" tick={{ fontSize: 11 }} width={55} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0f172a',
                    borderColor: '#334155',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  formatter={(val: any) => [`${val}%`, 'Capacity']}
                />
                <Bar dataKey="load" fill="#14b8a6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Patient Telemetry Cards */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <BedDouble className="h-5 w-5 text-teal-400" />
              <span>Real-Time Inpatient Telemetry Feeds</span>
            </h3>
            <p className="text-xs text-slate-400">Continuous biometric streams with automated triage scoring</p>
          </div>
          <span className="text-xs text-slate-400 font-mono">Showing {patients.length} Active Feeds</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {patients.map((pt) => {
            const isCritical = pt.status === 'Critical';

            return (
              <div
                key={pt.id}
                className={`p-5 rounded-2xl border transition relative overflow-hidden bg-slate-900/80 ${
                  isCritical
                    ? 'border-rose-500/50 shadow-lg shadow-rose-950/20'
                    : 'border-slate-800 hover:border-slate-700'
                }`}
              >
                {/* Header of Patient Card */}
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="text-base font-bold text-white">{pt.fullName}</h4>
                      <span className="text-xs font-mono text-slate-400">({pt.age}y / {pt.gender})</span>
                      <span
                        className={`text-[10px] font-mono px-2 py-0.5 rounded font-bold ${
                          isCritical
                            ? 'bg-rose-500 text-slate-950 animate-pulse'
                            : pt.status === 'Guarded'
                            ? 'bg-amber-400/20 text-amber-300 border border-amber-400/40'
                            : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                        }`}
                      >
                        {pt.status.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-xs text-teal-400 mt-1 flex items-center gap-2 font-mono">
                      <span>MRN: {pt.mrn}</span>
                      <span>•</span>
                      <span>{pt.department} ({pt.bedNumber})</span>
                    </p>
                  </div>

                  <div className="text-right">
                    <span className="text-[10px] font-mono uppercase text-slate-400">Triage ESI</span>
                    <div className="text-lg font-mono font-extrabold text-teal-300">Level {pt.triageScore}</div>
                  </div>
                </div>

                <div className="mt-3 p-2.5 rounded-xl bg-slate-950/60 border border-slate-800/80 text-xs">
                  <span className="text-slate-400">Primary Diagnosis: </span>
                  <span className="font-semibold text-slate-200">{pt.primaryDiagnosis}</span>
                </div>

                {/* Vital Telemetry Badges */}
                <div className="grid grid-cols-4 gap-2 mt-4">
                  <div className="bg-slate-950/60 p-2 rounded-xl border border-slate-800/80 text-center">
                    <div className="flex items-center justify-center gap-1 text-[10px] text-slate-400 mb-0.5">
                      <Heart className="h-3 w-3 text-rose-400" /> HR
                    </div>
                    <div className={`font-mono font-bold text-sm ${pt.vitals.heartRate > 100 ? 'text-rose-400' : 'text-slate-200'}`}>
                      {pt.vitals.heartRate} <span className="text-[9px] font-normal text-slate-400">bpm</span>
                    </div>
                  </div>

                  <div className="bg-slate-950/60 p-2 rounded-xl border border-slate-800/80 text-center">
                    <div className="flex items-center justify-center gap-1 text-[10px] text-slate-400 mb-0.5">
                      <Activity className="h-3 w-3 text-cyan-400" /> BP
                    </div>
                    <div className="font-mono font-bold text-sm text-slate-200">
                      {pt.vitals.bpSystolic}/{pt.vitals.bpDiastolic}
                    </div>
                  </div>

                  <div className="bg-slate-950/60 p-2 rounded-xl border border-slate-800/80 text-center">
                    <div className="flex items-center justify-center gap-1 text-[10px] text-slate-400 mb-0.5">
                      <Wind className="h-3 w-3 text-teal-400" /> SpO2
                    </div>
                    <div className={`font-mono font-bold text-sm ${pt.vitals.spO2 < 94 ? 'text-amber-400' : 'text-teal-400'}`}>
                      {pt.vitals.spO2}%
                    </div>
                  </div>

                  <div className="bg-slate-950/60 p-2 rounded-xl border border-slate-800/80 text-center">
                    <div className="flex items-center justify-center gap-1 text-[10px] text-slate-400 mb-0.5">
                      <Thermometer className="h-3 w-3 text-amber-400" /> Temp
                    </div>
                    <div className="font-mono font-bold text-sm text-slate-200">
                      {pt.vitals.tempC}°C
                    </div>
                  </div>
                </div>

                {/* Quick Action Footer */}
                <div className="mt-4 pt-3 border-t border-slate-800 flex flex-wrap items-center justify-between gap-2 text-xs">
                  <span className="text-[11px] text-slate-400">
                    Family: <strong className="text-slate-300 font-medium">{pt.familyContact.name}</strong> ({pt.familyContact.phone})
                  </span>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleLaunchPatientDigest(pt)}
                      className="px-3 py-1.5 rounded-lg bg-pink-500/15 hover:bg-pink-500/25 text-pink-300 border border-pink-500/30 transition flex items-center gap-1.5 text-xs font-semibold shadow-sm"
                    >
                      <MessageSquareHeart className="h-3.5 w-3.5 text-pink-400" />
                      <span>Generate Family Digest</span>
                    </button>

                    <button
                      onClick={() => onOpenDocForPatient(pt)}
                      className="px-3 py-1.5 rounded-lg bg-teal-500/10 hover:bg-teal-500/20 text-teal-300 border border-teal-500/30 transition flex items-center gap-1 text-xs font-semibold"
                    >
                      <Stethoscope className="h-3.5 w-3.5" />
                      <span>Generate SOAP</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
